"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function RegionalReach() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="238478" id="far-east-trade-network" className="text-foreground py-24 px-4 relative overflow-hidden bg-background light">
      {}
      <div className="absolute inset-0 bg-muted/30 opacity-40 pointer-events-none" />
      <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:32px_32px] opacity-10 pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        <div ref={ref} className={`text-center mb-16 transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          
          <h2 className="font-default text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight text-primary">Our Market Presence<br /><span className="text-muted-foreground" data-ast-id="f:/src/pages/Markets/sections/RegionalReach.tsx|sig:6hjyc0" data-ast-start="2195" data-ast-end="2381" contenteditable="false"></span></h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-default mb-4"><br />Our market presence reflects ongoing trade activity and regional partnerships across East and Southeast Asia.<br /><br /></p>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1667719/img/market presence map english.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1667719/img/market presence map english.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1667719/img/market presence map english.png-wvc-sizes" alt="Market presence visualization" className="w-full h-auto rounded-lg mb-4" loading="lazy" />
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-default"><br />Oriental World supports regional sourcing, logistics coordination, and industrial feedstock trade across key Asian markets. We continue to expand our regional trade network through long-term industry partnerships.<br /><br /></p>
          
          <div className="flex justify-center mt-6">
            <Link to="https://trusting-sunbird.10web.cloud/products/">
              <Button size="lg" className="group rounded-md px-8">
                View Products
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}